import clsx from 'clsx';
import { useState, Activity, useEffect, useCallback } from 'react';

import {
  IconSearch,
  IconReload,
  IconTrashX,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import CustomTable from '@/components/Table/index';
import { ERestoreEntityType } from '@/enum';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  restoreRecord,
  getDeletedRecords,
  deleteRecordPermanently,
} from '@/service/commonService';
import useCommonStore from '@/store/useCommonStore';

import DeletedRecordsFilterModal from '../modal/DeletedRecordsFilterModal';

interface IDeletedRecord {
  id: string;
  name: string;
  email: string;
  type: string;
  url: string;
  deleted_by: {
    id: string;
    name: string;
  };
  deleted_on: number;
}

const getTypeConfig = (type: string) => {
  const typeMapping: Record<string, { label: string; className: string }> = {
    school: {
      label: 'settings.deleted-records.types.school',
      className: 'bg-fuchsia-50 text-fuchsia-600',
    },
    teacher: {
      label: 'settings.deleted-records.types.teacher',
      className: 'bg-yellow-100 text-yellow-600',
    },
    parent: {
      label: 'settings.deleted-records.types.parent',
      className: 'bg-purple-50 text-purple-600',
    },
    student: {
      label: 'settings.deleted-records.types.student',
      className: 'bg-green-50 text-green-600',
    },
    child: {
      label: 'settings.deleted-records.types.child',
      className: 'bg-indigo-50 text-indigo-600',
    },
    admin: {
      label: 'settings.deleted-records.types.admin',
      className: 'bg-pink-50 text-pink-600',
    },
    class: {
      label: 'settings.deleted-records.types.class',
      className: 'bg-red-50 text-red-500',
    },
    lesson: {
      label: 'settings.deleted-records.types.lesson',
      className: 'bg-orange-50 text-orange-600',
    },
  };

  const lowerType = type.toLowerCase();
  return (
    typeMapping[lowerType] || {
      label: type || '-',
      className: 'bg-neutral-50 text-neutral-secondary',
    }
  );
};

const DeletedRecords = () => {
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const { loadingTable, setLoadingTable, loadingModal, setLoadingModal } =
    useCommonStore();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [records, setRecords] = useState<IDeletedRecord[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<{
    type?: ERestoreEntityType[];
    start_date?: number | null;
    end_date?: number | null;
  }>({});

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] =
    useState(false);
  const [selectedRecord, setSelectedRecord] = useState<IDeletedRecord | null>(
    null
  );
  const [sortColumn, setSortColumn] = useState('deleted_on');
  const [sortDirection, setSortDirection] = useState('DESC');

  const isFilterApplied =
    !!filterValues?.type ||
    !!filterValues?.start_date ||
    !!filterValues?.end_date;

  const isEmptyList =
    !loadingTable &&
    records?.length === 0 &&
    search?.length === 0 &&
    debouncedSearch?.length === 0 &&
    !isFilterApplied;

  const fetchRecords = useCallback(async () => {
    setLoadingTable(true);
    try {
      const response = await getDeletedRecords({
        search: debouncedSearch || undefined,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        order: sortColumn ? { [sortColumn]: sortDirection } : undefined,
        type: filterValues?.type,
        start_date: filterValues?.start_date || undefined,
        end_date: filterValues?.end_date || undefined,
      });
      if (response?.status) {
        setRecords(response?.data);
        setTotal(response?.total);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTable(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    currentPage,
    pageSize,
    filterValues,
    sortColumn,
    sortDirection,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleRestore = async () => {
    if (!selectedRecord) return;
    setLoadingModal(true);
    const response = await restoreRecord(selectedRecord?.id);
    if (response?.status) {
      fetchRecords();
    }
    setLoadingModal(false);
    setIsRestoreModalOpen(false);
    setSelectedRecord(null);
  };

  const handlePermanentDelete = async () => {
    if (!selectedRecord) return;
    setLoadingModal(true);
    const response = await deleteRecordPermanently({
      id: selectedRecord?.id,
      record_type: String(
        ERestoreEntityType[
          selectedRecord?.type?.toUpperCase() as keyof typeof ERestoreEntityType
        ]
      ),
    });
    if (response?.status) {
      fetchRecords();
    }
    setLoadingModal(false);
    setIsPermanentDeleteModalOpen(false);
    setSelectedRecord(null);
  };

  const getTypeNameStr = (type: string) => getTypeConfig(type).label;

  const getTypeClassNameStr = (type: string) => getTypeConfig(type).className;

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortColumn('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'settings.deleted-records.columns.names',
      key: 'name',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('name'),
      render: (row: IDeletedRecord) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[{ id: row?.id, name: row?.name, image: row?.url }]}
            size={40}
            avatarClassName="w-9 h-9 min-w-9"
          />
          <span className="text-sm font-medium text-neutral-primary">
            {row?.name}
          </span>
        </div>
      ),
    },
    {
      title: 'settings.deleted-records.columns.type',
      key: 'type',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('type'),
      render: (row: IDeletedRecord) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeClassNameStr(row?.type)}`}
        >
          {formatText(getTypeNameStr(row?.type))}
        </span>
      ),
    },
    {
      title: 'settings.deleted-records.columns.deleted-by',
      key: 'deleted_by',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('deleted_by'),
      render: (row: IDeletedRecord) => row?.deleted_by?.name || '-',
    },
    {
      title: 'settings.deleted-records.columns.deleted-on',
      key: 'deleted_on',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('deleted_on'),
      render: (row: IDeletedRecord) => formatDate(row?.deleted_on),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      align: 'center' as const,
      width: '80px',
      render: (row: IDeletedRecord) => {
        const record = row;

        const actions = [
          {
            key: 'restore',
            label: 'settings.deleted-records.actions.restore-btn',
            icon: <IconReload size={20} className="text-brand-hover" />,
            className: 'text-brand-hover!',
            onClick: () => {
              setSelectedRecord(record);
              setIsRestoreModalOpen(true);
            },
          },
          {
            key: 'delete-permanently',
            label: 'settings.deleted-records.actions.delete-btn',
            icon: <IconTrashX size={20} />,
            isDanger: true,
            onClick: () => {
              setSelectedRecord(record);
              setIsPermanentDeleteModalOpen(true);
            },
          },
        ];

        return <ActionDropdown actions={actions} className="w-max" />;
      },
    },
  ];

  return (
    <div className="space-y-6 w-full">
      {!isEmptyList && (
        <div className="flex justify-between items-center gap-4">
          <div className="flex flex-1 max-w-md gap-2.5 ps-1">
            <Input
              name="search"
              placeholder={formatText(
                'settings.deleted-records.search-placeholder'
              )}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="max-w-77 md:min-w-77 w-full"
              inputClassName="py-[13px]"
            />
            <div className="relative">
              <Button
                icon={<IconAdjustments size={20} />}
                label={formatText('common.actions.filter')}
                variant="outline"
                className={clsx(
                  'px-4 h-12 cursor-pointer',
                  isFilterApplied && 'border-brand-default! text-brand-default!'
                )}
                onClick={() => setIsFilterModalOpen(true)}
              />
              {isFilterApplied && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
              )}
            </div>
          </div>
        </div>
      )}
      <CustomTable
        columns={columns}
        dataSource={records || []}
        isLoading={loadingTable}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        fieldName={sortColumn}
        sortDirection={sortDirection}
        isEmpty={isEmptyList}
        emptyTitle={formatText('settings.deleted-records.not-found-title')}
        emptyDescription={formatText('settings.deleted-records.not-found-desc')}
        isEmptySearch={
          records?.length === 0 &&
          (search?.length > 0 || debouncedSearch?.length > 0 || isFilterApplied)
        }
      />
      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <DeletedRecordsFilterModal
          isOpen={isFilterModalOpen}
          setIsOpen={setIsFilterModalOpen}
          initialValues={filterValues}
          onApply={(values) => {
            setFilterValues(values);
            setCurrentPage(1);
          }}
        />
      </Activity>

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        setIsOpen={setIsRestoreModalOpen}
        title={formatText('settings.deleted-records.actions.restore-title')}
        descriptionPrefix={formatText(
          'settings.deleted-records.actions.restore-confirm'
        )}
        descriptionSuffix={formatText(
          'settings.deleted-records.actions.restore-confirm-suffix'
        )}
        confirmLabel={formatText(
          'settings.deleted-records.actions.restore-btn'
        )}
        onConfirm={handleRestore}
        isLoading={loadingModal}
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        setIsOpen={setIsPermanentDeleteModalOpen}
        title={formatText(
          'settings.deleted-records.actions.delete-permanently-title'
        )}
        descriptionPrefix={formatText(
          'settings.deleted-records.actions.delete-permanent-confirm'
        )}
        descriptionSuffix={formatText(
          'settings.deleted-records.actions.delete-permanent-confirm-suffix'
        )}
        confirmLabel={formatText('settings.deleted-records.actions.delete-btn')}
        onConfirm={handlePermanentDelete}
        isLoading={loadingModal}
        isDestructive
      />
    </div>
  );
};

export default DeletedRecords;
